import { useState } from 'react'
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
} from '@mui/material'
import { DateTime } from 'luxon'
import { formatUrl } from '@utils'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import SelectedRowDetailsDialog from '@components/SelectedRowDetailsDialog'
import { Org } from '@types'
import API from '@enums/api'
import useDataSource from '@hooks/useDataSource'
import CustomToolbar from '@components/CustomToolbar'

const columns: GridColDef<Org>[] = [
    {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 20,
        renderCell: (params: GridRenderCellParams<Org, string>) => (
            <div>
                <Link
                    href={
                        `/organizations/` + formatUrl(params.row.id as string)
                    }
                >
                    {params.value}
                </Link>
                <br />
                <span style={{ color: 'gray' }}>
                    {params.row.department || ''}
                </span>
            </div>
        ),
    },
    {
        field: 'created_date',
        headerName: 'Created Date',
        type: 'dateTime',
        flex: 12,
        valueFormatter: (value) =>
            DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT),
    },
    {
        field: 'address',
        headerName: 'Address',
        type: 'string',
        flex: 25,
        valueGetter: (_value, row) => {
            const { address, city, state, country, postcode } = row
            return [address, city, state, country, postcode]
                .filter(Boolean)
                .join(', ')
        },
    },
    {
        field: 'timezone',
        headerName: 'Time Zone',
        type: 'string',
        flex: 15,
    },
    {
        field: 'totalUsers',
        headerName: 'Total Users',
        type: 'number',
        flex: 15,
    },
    {
        field: 'review_date',
        headerName: 'Review Date',
        type: 'dateTime',
        flex: 12,
        valueFormatter: (value) =>
            DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT),
    },
    {
        field: 'denial_reason',
        headerName: 'Denial Reason',
        type: 'string',
        flex: 15,
    },
]

export default function OrganizationsDeniedOverviewPage() {
    const [error, setError] = useState<string | null>(null)
    const [downloadError, setDownloadError] = useState<string | null>(null)
    const [selectedRow, setSelectedRow] = useState<Org | null>(null)

    const { dataSource, exporting, exportAllRows, setFilterModel } =
        useDataSource({
            getRows: async ({ paginationModel, sortModel, filterModel }) => {
                const res = await API.overviews.organizations({
                    paginationModel,
                    sortModel,
                    filterModel,
                    approval: 'denied',
                })

                if (!res.ok) {
                    console.error(res)
                    setError(res.error.message)
                    throw new Error(res.error.message)
                }

                return {
                    rows: res.value.rows,
                    rowCount: res.value.total_rows,
                }
            },
        })

    if (error) {
        return <DashboardErrorView error={error} />
    }

    return (
        <DashboardLayout>
            <Dialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
            >
                <DialogTitle>{downloadError}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sometimes this can be resolved by logging out and back
                        in again. If that does not work, please reach out to one
                        of the development team and we will investigate the
                        issue.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDownloadError(null)} autoFocus>
                        Dismiss
                    </Button>
                </DialogActions>
            </Dialog>
            <SelectedRowDetailsDialog
                onClose={() => setSelectedRow(null)}
                selectedRow={selectedRow}
            />
            <DataGridPro
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                dataSource={dataSource}
                onFilterModelChange={(m) => setFilterModel(m)}
                disableVirtualization // Disabled in order to prevent row-ghosting
                onRowClick={(params) => {
                    setSelectedRow(params.row)
                }}
                showToolbar
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        exporting,
                        exportAllRows,
                        exportFilename: 'denied-org-applications',
                    },
                }}
            />
        </DashboardLayout>
    )
}
